import { ui } from "@/components/ui";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import AuthWrap from "@/features/auth/components/AuthWrap";
import type { QuickstartT } from "@/types/database/profiles";
import type { RoleT } from "@/types/zod/auth";
import { useForm as formspree } from "@formspree/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useSearch } from "@tanstack/react-router";
import { createFileRoute } from "@tanstack/react-router";
import { ExternalLink, Mail } from "lucide-react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import Call from "../../components/common/Call";
import Or from "../../components/common/Or";

const FormSchema = z.object({
	email: z
		.string()
		.min(1, { message: "This field has to be filled." })
		.email("This is not a valid email."),
});

const NewUser = () => {
	const { email } = useSearch({ from: "/_auth/new-user" });
	const form = useForm<z.infer<typeof FormSchema>>({
		resolver: zodResolver(FormSchema),
		defaultValues: {
			email: email || "",
		},
	});

	const [state, handleSubmit] = formspree("manojyoe");

	if (state.succeeded) {
		return (
			<AuthWrap>
				<ui.Card className="mx-auto w-full max-w-md">
					<ui.CardHeader>
						<ui.CardTitle>Thank You for Your Interest</ui.CardTitle>
						<ui.CardDescription>
							We'll notify you when Patient Watch is ready for you to try.
						</ui.CardDescription>
					</ui.CardHeader>
				</ui.Card>
			</AuthWrap>
		);
	}

	function onSubmit(values: z.infer<typeof FormSchema>) {
		handleSubmit(values);
	}

	return (
		<AuthWrap>
			<Call
				className="mb-4"
				size="sm"
				icon={<Mail />}
				words={`No account was found for: ${email}`}
			/>
			<ui.Card className="mx-auto w-full max-w-md">
				<ui.CardHeader className="flex flex-col gap-2">
					<ui.CardTitle>New User</ui.CardTitle>
				</ui.CardHeader>
				<ui.CardContent>
					<ui.Tabs defaultValue="patient" className="w-full">
						<ui.TabsList className="grid grid-cols-2 w-full">
							<ui.TabsTrigger value="patient">For Patients</ui.TabsTrigger>
							<ui.TabsTrigger value="clinician">For Clinicians</ui.TabsTrigger>
						</ui.TabsList>
						<ui.TabsContent value="patient" className="space-y-4 mt-4">
							<div className="space-y-4 text-md text-muted-foreground">
								<p>
									Patient Watch is an invite-only platform. If you've received
									an invitation from your healthcare team, please make sure
									you're using the exact email address that was used to send
									your invitation.
								</p>
								<p>
									If you believe you should have received an invitation, please
									contact your healthcare provider to ensure they have the
									correct email address.
								</p>
								<Separator />
								<p>
									Need help?{" "}
									<Link
										to="/support"
										className="text-indigo-600 underline underline-offset-2 hover:underline-offset-4"
									>
										Contact our support team
									</Link>
									.
								</p>
							</div>
						</ui.TabsContent>
						<ui.TabsContent value="clinician" className="space-y-4 mt-4">
							<div className="flex flex-col items-center gap-4">
								<div className="flex flex-col gap-2 w-full">
									<Form {...form}>
										<form
											onSubmit={form.handleSubmit(onSubmit)}
											className="space-y-4 w-full"
										>
											<FormField
												control={form.control}
												name="email"
												render={({ field }) => (
													<FormItem>
														<FormLabel>Join Waitlist</FormLabel>
														<FormControl>
															<Input
																placeholder="Enter your email"
																{...field}
															/>
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
											<ui.Button type="submit" className="w-full">
												{state.submitting ? "Joining..." : "Join Waitlist"}
											</ui.Button>
										</form>
									</Form>
									<Or />
									<ui.Button variant="secondary" asChild className="w-full">
										<a
											target="_blank"
											href="https://demo.patient-watch.com/try-demo"
											rel="noreferrer"
										>
											Try Demo <ExternalLink size={20} className="ml-1" />
										</a>
									</ui.Button>
								</div>
							</div>
						</ui.TabsContent>
					</ui.Tabs>
				</ui.CardContent>
			</ui.Card>
		</AuthWrap>
	);
};

export type OrganisationSummary = {
	organisation_id: string;
	organisation_name: string;
};

export type SignupSearch = {
	role?: RoleT;
	email?: string;
	organisation?: OrganisationSummary;
	quickstart?: QuickstartT;
};

export const Route = createFileRoute("/_auth/new-user")({
	component: NewUser,
});
